import type { TableDataShape } from '@utilities/components/TableData/utilities/types';
import type { ProportionStatGroup } from '@utilities/helpers/significance';

import {
  COMPARE_BY_ACCESSOR,
  getColumnAccessor,
  OPTION_LABEL_ACCESSORS,
  TOTAL_RESPONSES_ACCESSOR,
} from '@api/survey_dashboard/survey_responses/utilities/columns';
import { computeProportionStatTest, indexToColumnLetter } from '@utilities/helpers/significance';

import type { GridSignificanceResult } from '../../Grid/utilities/gridSignificanceAdapter';

/** Row key tying together the compare-by segment rows of one answer option (or box row). */
export const MONADIC_ROW_GROUP_ACCESSOR = 'row_group_key';
/** Row field mapping each concept accessor to that segment's respondent base for the concept. */
export const MONADIC_CONCEPT_BASES_KEY = 'concept_bases';

const NON_CONCEPT_ACCESSORS = [
  ...OPTION_LABEL_ACCESSORS,
  COMPARE_BY_ACCESSOR,
  TOTAL_RESPONSES_ACCESSOR,
  MONADIC_ROW_GROUP_ACCESSOR,
];

/**
 * Stable identity of a monadic compare-by row: its answer-option group plus its
 * segment. Neither part is unique alone — option ids repeat once per segment and
 * are null on both Top/Bottom box rows.
 */
export const getMonadicSignificanceRowKey = (row: Record<string, unknown> | undefined): string => {
  const group = row?.[MONADIC_ROW_GROUP_ACCESSOR];
  const segment = row?.[COMPARE_BY_ACCESSOR];
  if (group == null || group === '' || segment == null || segment === '') return '';
  return `${String(group)}\u001F${String(segment)}`;
};

/**
 * Letters the compare-by segments of a monadic-section single/multi-select
 * `tableCount` table against each other, within each concept column.
 *
 * The table carries one row per answer option AND segment, with one column per
 * concept. Each row's `concept_bases` holds the segment's respondent base per
 * concept, which is the z-test denominator (`total_respondents` is the segment
 * max across concepts, so it is wrong for any single concept). Concepts are never
 * compared against each other: in a sequential monadic survey the same
 * respondents see every concept, so concept samples are not independent.
 *
 * Segments keep a consistent first-appearance letter throughout the table, and
 * Top/Bottom box rows are tested like any other option.
 *
 * @param tableCount - The question's count table.
 * @param alpha - The significance level used by the proportion test.
 * @returns Annotations in the grid result shape (concept accessors in place of
 * option accessors), or `null` when the table has no compare-by segments to
 * compare or its rows can't be identified reliably.
 */
export function computeMonadicSegmentSignificance(
  tableCount: null | TableDataShape | undefined,
  alpha?: number
): GridSignificanceResult | null {
  const columns = tableCount?.columns;
  const rows = tableCount?.data;
  if (!columns?.length || !rows?.length) return null;

  const accessors = columns
    .map((column) => getColumnAccessor(column))
    .filter((accessor): accessor is string => typeof accessor === 'string');
  if (!accessors.includes(COMPARE_BY_ACCESSOR)) return null;
  const conceptAccessors = accessors.filter(
    (accessor) => !NON_CONCEPT_ACCESSORS.includes(accessor)
  );
  if (!conceptAccessors.length) return null;

  const segmentIndexByName = new Map<string, number>();
  const optionGroups = new Map<
    string,
    { row: Record<string, unknown>; rowKey: string; segmentIndex: number }[]
  >();
  const seenRowKeys = new Set<string>();

  for (const row of rows) {
    const rowKey = getMonadicSignificanceRowKey(row);
    // Missing or duplicate row identity would misattribute letters — degrade to
    // no annotations rather than wrong ones.
    if (rowKey === '' || seenRowKeys.has(rowKey)) return null;
    seenRowKeys.add(rowKey);

    const segmentName = String(row[COMPARE_BY_ACCESSOR]);
    if (!segmentIndexByName.has(segmentName)) {
      segmentIndexByName.set(segmentName, segmentIndexByName.size);
    }
    const segmentIndex = segmentIndexByName.get(segmentName) as number;

    const groupKey = String(row[MONADIC_ROW_GROUP_ACCESSOR]);
    const groupRows = optionGroups.get(groupKey) ?? [];
    groupRows.push({ row, rowKey, segmentIndex });
    optionGroups.set(groupKey, groupRows);
  }

  if (segmentIndexByName.size < 2) return null;

  const segmentCount = segmentIndexByName.size;
  const cellHigherThanByKey: Record<string, Record<string, string[]>> = Object.create(null);
  const rowLetterByKey: Record<string, null | string> = Object.create(null);

  optionGroups.forEach((groupRows) => {
    let isGroupTested = false;
    for (const { rowKey } of groupRows) cellHigherThanByKey[rowKey] = Object.create(null);

    // Each concept has its own per-segment bases, so each is a separate test.
    conceptAccessors.forEach((accessor) => {
      const groups: ProportionStatGroup[] = Array.from({ length: segmentCount }, () => ({
        n: 0,
        options: [{ count: null }],
      }));
      for (const { row, segmentIndex } of groupRows) {
        const bases = row[MONADIC_CONCEPT_BASES_KEY] as Record<string, unknown> | undefined;
        groups[segmentIndex] = {
          n: toFiniteNumber(bases?.[accessor]) ?? 0,
          options: [{ count: toFiniteNumber(row[accessor]) }],
        };
      }

      const result = computeProportionStatTest({ groups }, alpha);
      if (!result) return;
      isGroupTested = true;

      for (const { rowKey, segmentIndex } of groupRows) {
        const letters = result.cellHigherThan[0]?.[segmentIndex] ?? [];
        if (letters.length) cellHigherThanByKey[rowKey][accessor] = letters;
      }
    });

    for (const { rowKey, segmentIndex } of groupRows) {
      rowLetterByKey[rowKey] = isGroupTested ? indexToColumnLetter(segmentIndex) : null;
    }
  });

  return { cellHigherThanByKey, optionAccessors: conceptAccessors, rowLetterByKey };
}

function toFiniteNumber(value: unknown): null | number {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
